import type { FluentProviderProps } from '@fluentui/react-components';

/**
 * Alto de los controles de formulario de la guía (40 px).
 *
 * Fluent v9 no expone el tamaño de un control como clase CSS: los estilos son atómicos. Estos
 * hooks de estilo se registran una sola vez en el `FluentProvider` de la raíz y marcan con
 * `GUIDE_CONTROL_CLASS` sólo los controles de tamaño `medium` (el de por defecto); el alto lo
 * pone `theme.css` con la variable `--ichtys-control-height`, que vuelve a 32 px dentro de
 * tablas, grillas, barras de herramientas y banners. Los controles `small`/`large` y los botones
 * sólo-ícono, sutiles o transparentes no se tocan.
 */
export const GUIDE_CONTROL_CLASS = 'ichtys-control';

type Slot = { className?: string } | undefined;
type SizedState = { size?: string; root: Slot };
type ButtonState = SizedState & { appearance?: string; iconOnly?: boolean };

const SIZED_BUTTON_APPEARANCES = new Set(['secondary', 'primary', 'outline']);

const markRoot = (state: SizedState) => {
  if (!state.root) return;
  state.root.className = state.root.className ? `${state.root.className} ${GUIDE_CONTROL_CLASS}` : GUIDE_CONTROL_CLASS;
};

/** Inputs, dropdowns, comboboxes, selects y spin buttons de tamaño medium (incluye el input del DatePicker). */
const markMediumField = (state: unknown) => {
  const s = state as SizedState;
  if ((s.size ?? 'medium') === 'medium') markRoot(s);
};

/** Botones estándar (primario, secundario, contorno) de tamaño medium y con texto. */
const markMediumButton = (state: unknown) => {
  const s = state as ButtonState;
  if ((s.size ?? 'medium') !== 'medium' || s.iconOnly) return;
  if (!SIZED_BUTTON_APPEARANCES.has(s.appearance ?? 'secondary')) return;
  markRoot(s);
};

export const guideControlStyleHooks: NonNullable<FluentProviderProps['customStyleHooks_unstable']> = {
  useInputStyles_unstable: markMediumField,
  useDropdownStyles_unstable: markMediumField,
  useComboboxStyles_unstable: markMediumField,
  useSelectStyles_unstable: markMediumField,
  useSpinButtonStyles_unstable: markMediumField,
  useButtonStyles_unstable: markMediumButton,
  useMenuButtonStyles_unstable: markMediumButton,
};
